import React, { useState, useEffect, useRef } from 'react';
import { View, ActivityIndicator, Alert } from 'react-native';
import { styles, colors } from './styles';
import ConfigForm from './components/ConfigForm';
import DialerInterface from './components/DialerInterface';
import { getUserInfo, initializeExtension, checkConfig } from './services';

const App = () => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [userId, setUserId] = useState(null);
  const [systemId, setSystemId] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ramal, setRamal] = useState(null);
  
  // Refs para prevenir requisições duplicadas
  const isCheckingConfigRef = useRef(false);
  // Ref para armazenar se estava configurado antes de entrar na tela de configuração
  const wasConfiguredRef = useRef(false);
  // Ref para prevenir eventos duplicados (trava para eventos do WlExtension)
  const lastEventRef = useRef({ type: null, contactId: null, timestamp: 0 });
  // Ref para rastrear último contactId processado (evita atualizações duplicadas)
  const lastProcessedContactIdRef = useRef(null);

  // Inicialização: obter informações do usuário e verificar configuração
  useEffect(() => {
    const initialize = async () => {
      try {
        // 1. Obter informações do usuário (aguarda WlExtension estar disponível)
        // Retorna { userId, systemKey } conforme documentação
        const userInfo = await getUserInfo();
        setUserId(userInfo.userId);
        setSystemId(userInfo.systemKey); // systemKey, não systemId

        // 2. Verificar configuração
        const config = await checkConfig(userInfo.userId);
        setIsConfigured(config.configured || false);
        if (config.ramal) {
          setRamal(config.ramal);
        }
      } catch (err) {
        console.error('Erro na inicialização:', err);
        // Se o erro for sobre WlExtension não disponível, apenas loga
        // A extensão pode funcionar em modo de configuração mesmo sem WlExtension
        if (err.message && err.message.includes('WlExtension')) {
          console.warn('WlExtension ainda não está disponível. A extensão funcionará em modo limitado.');
          // Permite continuar sem userId para permitir configuração manual
        } else if (err.message && err.message.includes('check-config')) {
          // Se o erro for na verificação de configuração, assume que não está configurado
          console.warn('Erro ao verificar configuração. Assumindo que não está configurado.');
          setIsConfigured(false);
        } else {
          // Apenas mostra alerta para erros críticos
          console.warn('Erro na inicialização, continuando em modo limitado:', err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  // Configurar eventos chatlabel quando estiver configurado
  // Reconfigurar sempre que isConfigured mudar para true (incluindo quando voltar das configurações)
  useEffect(() => {
    if (isConfigured) {
      let isMounted = true;
      
      const updateContactInfo = (attendance, eventType) => {
        if (!isMounted) return;
        
        const contactId = attendance.contactId || attendance.atendimentoId || attendance.id || null;
        const now = Date.now();
        
        // Trava 1: Prevenir eventos duplicados (mesmo tipo, mesmo contactId, em menos de 200ms)
        // Aumentado para 200ms para capturar eventos duplicados do WlExtension
        if (
          lastEventRef.current.type === eventType &&
          lastEventRef.current.contactId === contactId &&
          (now - lastEventRef.current.timestamp) < 200
        ) {
          console.log(`[TRAVA] Evento ${eventType} duplicado ignorado para contactId ${contactId} (${now - lastEventRef.current.timestamp}ms desde último)`);
          return;
        }
        
        // Trava 2: Se o contactId não mudou e já foi processado recentemente, ignorar
        // Isso evita atualizações desnecessárias quando o mesmo chat recebe múltiplos eventos
        if (
          lastProcessedContactIdRef.current === contactId &&
          (now - lastEventRef.current.timestamp) < 500 &&
          eventType !== 'onClose'
        ) {
          console.log(`[TRAVA] ContactId ${contactId} já processado recentemente, ignorando evento ${eventType}`);
          return;
        }
        
        // Atualizar referências
        lastEventRef.current = {
          type: eventType,
          contactId: contactId,
          timestamp: now
        };
        lastProcessedContactIdRef.current = contactId;
        
        console.log(`[EVENTO] Processando ${eventType} para contactId ${contactId}`);
        
        setContactInfo({
          nome: attendance.contato?.nome || attendance.name || attendance.nome || '',
          nomeSecundario: attendance.contato?.nomeSecundario || attendance.secondaryName || attendance.nomeSecundario || null,
          numero: attendance.contato?.numero || attendance.numero || attendance.phone || attendance.telefone || attendance.identificadorChat || '',
          linkImagem: attendance.contato?.linkImagem || attendance.linkImagem || attendance.avatar || attendance.profilePicture || null,
          protocolo: attendance.protocolo || null,
          contactId: contactId,
        });
      };
      
      const setupEvents = async () => {
        try {
          // Limpar contato atual ao reconfigurar eventos (para garantir estado limpo)
          setContactInfo(null);
          
          await initializeExtension({
            onOpen: (attendance) => {
              if (!isMounted) return;
              console.log('Chat aberto:', attendance);
              updateContactInfo(attendance, 'onOpen');
            },
            onFocus: (attendance) => {
              if (!isMounted) return;
              console.log('Chat em foco:', attendance);
              updateContactInfo(attendance, 'onFocus');
            },
            onClose: (attendance) => {
              if (!isMounted) return;
              console.log('Chat fechado:', attendance);
              
              const contactId = attendance?.contactId || attendance?.atendimentoId || attendance?.id || null;
              const now = Date.now();
              
              // Trava: Prevenir eventos duplicados (aumentado para 200ms)
              if (
                lastEventRef.current.type === 'onClose' &&
                lastEventRef.current.contactId === contactId &&
                (now - lastEventRef.current.timestamp) < 200
              ) {
                console.log(`[TRAVA] Evento onClose duplicado ignorado para contactId ${contactId} (${now - lastEventRef.current.timestamp}ms desde último)`);
                return;
              }
              
              // Atualizar referências
              lastEventRef.current = {
                type: 'onClose',
                contactId: contactId,
                timestamp: now
              };
              lastProcessedContactIdRef.current = null; // Limpar quando fechar
              
              console.log(`[EVENTO] Processando onClose para contactId ${contactId}`);
              
              setContactInfo(null);
            }
          });
        } catch (err) {
          if (isMounted) {
            console.error('Erro ao configurar eventos:', err);
          }
        }
      };
      
      setupEvents();
      
      return () => {
        isMounted = false;
      };
    } else {
      // Quando não está configurado, limpar contato
      setContactInfo(null);
    }
  }, [isConfigured]);

  const handleConfigSave = () => {
    setIsConfigured(true);
    // Recarregar configuração para obter o ramal
    if (userId && !isCheckingConfigRef.current) {
      isCheckingConfigRef.current = true;
      checkConfig(userId)
        .then((config) => {
          if (config.ramal) {
            setRamal(config.ramal);
          }
        })
        .catch(console.error)
        .finally(() => {
          isCheckingConfigRef.current = false;
        });
    }
  };

  const handleSettingsClick = () => {
    // Armazenar se estava configurado antes de entrar na tela de configuração
    wasConfiguredRef.current = isConfigured;
    setIsConfigured(false);
  };

  const handleConfigCancel = () => {
    // Se já estava configurado antes, volta para a tela principal imediatamente
    // Se não estava configurado, não faz nada (fica na tela de configuração)
    if (wasConfiguredRef.current) {
      setIsConfigured(true);
      // Atualizar ramal em background se necessário
      if (userId && !isCheckingConfigRef.current) {
        isCheckingConfigRef.current = true;
        checkConfig(userId)
          .then((config) => {
            if (config.ramal) {
              setRamal(config.ramal);
            }
          })
          .catch(console.error)
          .finally(() => {
            isCheckingConfigRef.current = false;
          });
      }
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        <ActivityIndicator size="large" color={colors.textPrimary} />
      </View>
    );
  }

  if (!isConfigured) {
    return (
      <ConfigForm
        userId={userId}
        systemKey={systemId}
        onSave={handleConfigSave}
        onCancel={handleConfigCancel}
      />
    );
  }

          return (
            <DialerInterface
              contactInfo={contactInfo}
              onSettingsClick={handleSettingsClick}
              userId={userId}
              ramal={ramal}
              systemKey={systemId}
            />
          );
};

export default App;

